import moment from 'moment-timezone';

export const ISO_DATE_FORMAT = 'YYYY-MM-DD';
export const ISO_DATETIME_NO_TIMEZONE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
export const ISO_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
export const ISO_DATETIME_MICROSECONDS_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSSSS';
export const MM_DD_YYYY_DATE_FORMAT = 'MM/DD/YYYY';
export const MM_DD_YYYY_HH_MM_SS_A_FORMAT = 'MM/DD/YYYY hh:mm:ss A';
export const MM_DD_YYYY_AT_HH_MM_A_FORMAT = 'MM/DD/YYYY [at] hh:mm A';
export const MM_DD_YY_DATE_FORMAT = 'MM/DD/YY';
export const DD_MMM_YY_DATE_FORMAT = 'DD-MMM-YY';
export const YYYYMMDD_DATE_FORMAT = 'YYYYMMDD';
export const M_D_YYYY_DATE_FORMAT = 'M/D/YYYY';
export const M_D_YY_DATE_FORMAT = 'M/D/YY';
export const READABLE_DATE_FORMAT = 'MMM DD, YYYY';

export function convertDateFormat(date: string, inputFormat: string, outputFormat: string): string {
  const mDate = moment.tz(date, inputFormat, true, 'America/New_York');
  if (!mDate.isValid()) {
    return '';
  }

  return mDate.format(outputFormat);
}

export function parseDate(
  rawDate: unknown,
  outputFormat: string,
  ...inputFormats: string[]
): string {
  let date = rawDate;
  if (typeof date === 'number') {
    date = `${date}`;
  }

  if (!date || (typeof date !== 'string' && !(date instanceof Date)) || date === '') {
    return '';
  }

  let mDate: moment.Moment | undefined;
  if (date instanceof Date) {
    const dateStr = moment(date).format().slice(0, ISO_DATETIME_NO_TIMEZONE_FORMAT.length);
    mDate = moment.tz(dateStr, ISO_DATETIME_NO_TIMEZONE_FORMAT, true, 'America/New_York');
  } else {
    for (const inputFormat of inputFormats) {
      mDate = moment.tz(date, inputFormat, true, 'America/New_York');
      if (mDate.isValid()) {
        break;
      }
    }
  }

  if (!mDate || !mDate.isValid()) {
    return '';
  }

  return mDate.format(outputFormat);
}

/** Formats an ISO-8601 date-time (any fractional precision) in Eastern Time; zone-less values are treated as Eastern. */
export function formatIsoDateTimeToEst(rawDate: unknown, outputFormat: string): string {
  if (typeof rawDate !== 'string' || !rawDate.trim()) {
    return '';
  }

  const mDate = moment.tz(rawDate.trim(), moment.ISO_8601, 'America/New_York');
  return mDate.isValid() ? mDate.format(outputFormat) : '';
}

export function formatUnixTimestampToEst(timestamp: number, format: string = 'MM/DD/YYYY'): string {
  return moment.tz(timestamp, 'America/New_York').format(format);
}

export function formatDateToShortFormat(dateInput: string | Date | null | undefined): string {
  if (!dateInput) return '';
  const parsedDate = parseDate(
    dateInput,
    MM_DD_YY_DATE_FORMAT,
    ISO_DATETIME_MICROSECONDS_FORMAT,
    ISO_DATETIME_FORMAT,
    ISO_DATETIME_NO_TIMEZONE_FORMAT,
    ISO_DATE_FORMAT,
    MM_DD_YYYY_DATE_FORMAT,
  );
  return parsedDate;
}

/**
 * Determines whether the current time is at or past a cutoff time expressed in Eastern Time
 * (`America/New_York`), where the cutoff is calculated as a number of calendar days before a
 * given base timestamp. Always evaluates using Eastern Time regardless of the browser/machine
 * local timezone.
 *
 * @param baseTimestamp unix timestamp (ms) the cutoff date is calculated relative to (e.g. a settlement date)
 * @param daysBeforeBase number of calendar days before the base timestamp's Eastern date the cutoff falls on
 * @param cutoffHour the Eastern Time hour (0-23) at which the cutoff takes effect
 * @param nowTimestamp unix timestamp (ms) to treat as "now"; defaults to the actual current time
 */
export function isPastEasternTimeCutoff(
  baseTimestamp: number,
  daysBeforeBase: number,
  cutoffHour: number,
  nowTimestamp: number = Date.now(),
): boolean {
  const cutoff = moment
    .tz(baseTimestamp, 'America/New_York')
    .subtract(daysBeforeBase, 'day')
    .set({ hour: cutoffHour, minute: 0, second: 0, millisecond: 0 });
  return moment.tz(nowTimestamp, 'America/New_York').isSameOrAfter(cutoff);
}
